import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { createContext, useCallback, useEffect, useState } from 'react';
import { useLocalStorage, useRefresh } from '@/hooks';
import { getObjectDiff, getUniversityAndOverrideUrl, getUniversityWithFlattedUrl } from '@/utils';
import { UniversityHttp } from '@/services';
import { envConfig } from '@/configs';
import appStore from '@/store/app.store';

export interface UniversityContextType {
  error: string;
  loading: boolean;
  refresh: () => void;
  universities: UniversityEntityOriginal[];
  university: UniversityEntity;
  setUniversity: (university: UniversityEntity) => void;
}

export const UniversityContext = createContext<UniversityContextType>({
  loading: false,
  error: null,
  refresh: null,
  universities: [],
  university: {} as UniversityEntity,
  setUniversity: null
});

export function UniversityContextProvider({ children }) {

  const [t] = useTranslation(['common']);
  const [refreshCounter, refresh] = useRefresh();
  const [universities, setUniversities] = useState<UniversityEntityOriginal[]>([]);
  const [university, setUniversity] = useLocalStorage<UniversityEntity>('UNIVERSITY', {} as UniversityEntity);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUniversities = useCallback(async () => {
    setLoading(true);
    try {
      const res = await UniversityHttp.fetchList();
      setUniversities(res.data);
    }
    catch (e) {
      setError(e);
      console.error(e);
    }
    finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (loading || university == null || university?.unikey == null) return;
    const uni = universities.find(u => u?.unikey === university?.unikey);
    const uniUrlArray = (typeof uni?.universityURL === 'string' ? [uni?.universityURL] : uni?.universityURL) ?? [];
    for (const url of uniUrlArray) {
      if (url === university.universityURL) {
        const newUni = getUniversityAndOverrideUrl(uni, url);
        if (getObjectDiff(newUni, university).length > 0) {
          setUniversity(newUni);
        }
        return;
      }
    }
    const newUni = getUniversityWithFlattedUrl(uni);
    setUniversity(newUni);
    notification.info({ message: 'لینک سرویس منقضی شده است، بارگزاری مجدد صفحه با لینک جدید' });
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  }, [university, loading, setUniversities]);

  useEffect(() => {
    if (envConfig.serveMode === 'cloud') {
      fetchUniversities();
      return () => { };
    }
    const conf = envConfig.onPremise;
    const uni: UniversityEntity = {
      onPremiseUrl: conf?.samadAPIUrl,
      unikey: envConfig?.onPremise?.orgKey,
      universityLabel: t('common:app.customer-name'),
      universityURL: conf?.samadAPIUrl,
      autoLogoutTime: conf?.core?.auth?.autoLogoutTime ?? -1,
      enableCaptcha: conf?.core?.auth?.enableCaptcha ?? -1,
      casUrl: conf?.core?.auth?.casEnable ? conf?.core?.auth?.casUrl : '',
      forceCas: conf?.core?.auth?.casForce ?? false,
      grantType: conf?.core?.auth?.grantType ?? 'password',
      services: {
        feedbackProjectId: conf?.core?.feedbackId ? Number(conf?.core?.feedbackId) : null,
        help_desk: conf?.helpdesk?.url,
        washer: 0,
        booklet: 0,
        SnappFood: 0
      },
      samad: {
        Clinics: conf?.modules?.health,
        ActivityReport: conf?.core?.activityReportEnable,
        Attendance: true,
        dormitory: conf?.modules?.dormitory,
        dormitoryVersion: conf?.modules?.dormitoryVersion ?? 1,
        transport: conf?.modules?.transport,
        nurture: conf?.modules?.food,
        sport: conf?.modules?.sport,
        Survey: conf?.modules?.survey,
        faceEnrollment: conf?.modules?.face,
        faceEnrollmentType: conf?.face?.type as any,
        faceEnrollmentURL: conf?.face?.url,
        help_desk: conf?.helpdesk?.url,
        lastsecond: 1,
        cvv2: !envConfig.ui.removeCVV2,
        changePassword: !envConfig.ui.removeChangePassword,
        forceupdate: 0,
        QRscanner: conf?.core?.QRscanner,
        teammate: conf?.core?.teammateEnable
      }
    };
    setUniversity(uni);
    setUniversities([uni]);
    setLoading(false);
  }, [fetchUniversities, refreshCounter, setUniversity]);

  useEffect(() => {
    if (university?.unikey) {
      appStore.getState().fetchApiVersion();
    }
  }, [university?.unikey])


  return (
    <UniversityContext.Provider value={{
      error,
      loading,
      refresh,
      universities,
      university,
      setUniversity
    }}>
      {children}
    </UniversityContext.Provider>
  );
}
